import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { AlertTriangle, GraduationCap, Info, KeyRound, Smartphone } from 'lucide-react';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/common/Input';
import PageHero from '../components/common/PageHero';
import { toast } from 'react-toastify';
import api from '../services/api';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';

const AlumniLogin = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: '',  // This should be mobile number
    password: ''
  });

  // Check if already logged in
  useEffect(() => {
    const token = localStorage.getItem('alumni_token');
    if (token) {
      navigate('/alumni/dashboard');
    }
  }, [navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('Form submission - Username:', formData.username);
    console.log('Form submission - Password length:', formData.password?.length);

    if (!formData.username || !formData.password) {
      toast.error('Please enter both mobile number and password');
      return;
    }

    // Validate mobile number format (Kenyan phone number)
    const mobileRegex = /^07[0-9]{8}$/;
    if (!mobileRegex.test(formData.username)) {
      toast.error('Please enter a valid mobile number (e.g., 0712345678)');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/alumni/login', {
        username: formData.username,  // This should be the mobile number
        password: formData.password
      });

      if (response.data.success) {
        localStorage.setItem('alumni_token', response.data.token);
        localStorage.setItem('alumni_user', JSON.stringify(response.data.user));
        
        toast.success('Login successful! Redirecting to dashboard...');

        setTimeout(() => {
          navigate('/alumni/dashboard');
        }, 1500);
      } else {
        toast.error(response.data.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      if (error.response?.status === 401) {
        toast.error('Invalid credentials. Please check your mobile number and admission number.');
      } else if (error.response?.status === 403) {
        toast.error('Access denied. This portal is for alumni only.');
      } else {
        toast.error(error.response?.data?.message || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Header />
      <div className="min-h-[calc(100vh-8rem)] bg-ink-50">
        <PageHero
          icon={GraduationCap}
          title="Alumni Portal Login"
          subtitle="Access your alumni account with your mobile number and admission number"
        />

        <div className="container-page py-8 sm:py-12">
          <div className="mx-auto max-w-md">
            <Card className="p-6 sm:p-8">
              <form onSubmit={handleSubmit}>
                <div className="space-y-5">
                  <div>
                    <label className="label inline-flex items-center gap-1.5">
                      <Smartphone className="h-4 w-4 text-ink-500" aria-hidden="true" />
                      Mobile Number (Username)
                    </label>
                    <Input
                      type="tel"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      placeholder="0712345400"
                      required
                    />
                    <p className="mt-1 text-xs text-ink-500">Enter your registered mobile number (e.g., 0712345400)</p>
                  </div>

                  <div>
                    <label className="label inline-flex items-center gap-1.5">
                      <KeyRound className="h-4 w-4 text-ink-500" aria-hidden="true" />
                      Password (Admission Number)
                    </label>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      placeholder="CEE/301/S2023"
                      required
                    />
                    <p className="mt-1 text-xs text-ink-500">Enter your admission number as password (e.g., CEE/301/S2023)</p>
                  </div>

                  <Button type="submit" className="w-full py-3" disabled={loading}>
                    {loading ? 'Logging in...' : 'Login to Alumni Portal'}
                  </Button>
                </div>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-ink-500">
                  Not registered yet?{' '}
                  <Link to="/alumni/register" className="font-semibold text-brand-700 hover:underline">
                    Register as Alumnus
                  </Link>
                </p>
              </div>

              <div className="mt-4 rounded-xl border border-gold-200 bg-gold-50 p-3">
                <p className="text-xs text-gold-900">
                  <strong className="inline-flex items-center gap-1.5">
                    <Info className="h-3.5 w-3.5" aria-hidden="true" />
                    Test Credentials:
                  </strong><br />
                  • Mobile Number: <span className="font-mono font-bold">0712345400</span><br />
                  • Password: <span className="font-mono font-bold">CEE/301/S2023</span><br />
                  <span className="mt-1 flex items-start gap-1.5 text-xs">
                    <AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
                    Make sure to enter the mobile number (not the admission number) in the first field
                  </span>
                </p>
              </div>
            </Card>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default AlumniLogin;
